import { motion } from 'framer-motion';

const safetyPillars = [
  {
    icon: 'ri-shield-cross-line',
    title: 'Drug Interaction Screening',
    desc: 'Every prescription we fill is screened against your complete medication profile using advanced pharmacy software. Our pharmacists check for potential drug-drug interactions, drug-allergy conflicts, and duplicate therapies before dispensing — catching issues that could otherwise lead to adverse reactions or hospitalization.',
  },
  {
    icon: 'ri-file-search-line',
    title: 'Comprehensive Medication Reviews (MedsCheck)',
    desc: 'Our pharmacists conduct thorough, one-on-one medication reviews covered by the Ontario MedsCheck program. During a 30-minute private consultation, we review all your prescription and over-the-counter medications, identify potential concerns, and provide a personalized action plan to optimize your therapy. This service is free for eligible Ontario residents.',
  },
  {
    icon: 'ri-calendar-todo-line',
    title: 'Blister Packaging & Adherence Support',
    desc: 'For patients taking multiple medications, we offer customized blister packaging that organizes doses by day and time. This simple but powerful tool significantly reduces missed doses and medication errors. Combined with automatic refill reminders and synchronized prescription renewals, we make staying on track effortless.',
  },
  {
    icon: 'ri-parent-line',
    title: 'Pediatric & Geriatric Dosing',
    desc: 'Children and seniors require special attention when it comes to medication. Our pharmacists carefully verify weight-based pediatric dosing and assess age-related changes in kidney and liver function for older adults, adjusting recommendations to ensure safe, effective treatment for every age group.',
  },
];

export default function PharmacyMedicationSafety() {
  return (
    <section aria-labelledby="safety-heading" className="mb-20">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="mb-10"
      >
        <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-4">Your Safety</p>
        <h2 id="safety-heading" className="text-3xl font-bold text-foreground-950 mb-4 leading-snug">
          Medication Safety &amp; Personalized Management
        </h2>
        <p className="text-foreground-500 max-w-3xl text-[0.94rem] leading-relaxed">
          At <strong>Thornhill Drug-Mart</strong>, medication safety is our highest priority. Beyond simply filling prescriptions, our pharmacists actively monitor your complete health profile to prevent adverse drug events, optimize therapeutic outcomes, and provide ongoing education. Our comprehensive medication management program is designed to give you confidence that every dose you take is safe, appropriate, and effective for your unique health needs.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {safetyPillars.map((item, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.09, ease: 'easeOut' }}
            className="bg-background-50 border border-background-200 rounded-lg p-6"
          >
            <div className="w-10 h-10 flex items-center justify-center text-primary-600 mb-4" aria-hidden="true">
              <i className={`${item.icon} text-2xl`}></i>
            </div>
            <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
          </motion.article>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55, delay: 0.35, ease: 'easeOut' }}
        className="mt-8 bg-gradient-to-r from-teal-50 to-white border border-teal-100 rounded-lg p-6 sm:p-8"
      >
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-lightbulb-flash-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground-950 mb-2">Proper Medication Storage &amp; Disposal</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Did you know that storing medications in bathroom cabinets can reduce their effectiveness due to heat and humidity? Our pharmacists provide guidance on proper storage conditions to maintain medication potency. We also serve as a safe disposal site — bring your expired or unused medications to our pharmacy for environmentally responsible disposal, keeping them out of landfills, waterways, and the wrong hands.
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  );
}